import React from 'react';
import { motion } from 'framer-motion';
import { UtensilsCrossed, Smile, Heart, Users } from 'lucide-react';
import c2Image from '../assets/c4.jpg';
import nourishmentIcon from '../assets/icon2.png';
import goodnessIcon from '../assets/icon3.png';
import flavorsIcon from '../assets/icon1.png';
import careIcon from '../assets/icon4.png';

const KidsToothpasteSection = () => {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 1, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const iconStagger = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const iconItem = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className='mb-10'>
      <section className="relative overflow-hidden rounded-[60px] mt-20 mb-10 bg-cover bg-center mx-[5%]"
        style={{ backgroundImage: `url(${require('../assets/about.png')})` }}>
        
        <div className="max-w-8xl mx-auto flex flex-col md:flex-row items-center justify-between px-6 md:px-16 py-16 md:py-24 text-white lg:px-[5%]">
          {/* Left Content */}
          <motion.div 
            className="md:w-1/2 space-y-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
              variants={fadeInLeft}
            >
              Crafted with care, <br />
              made for comfort, <br />
              loved by kids
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-blue-100 max-w-md"
              variants={fadeInLeft}
            >
              A gentle toothpaste made with safe ingredients and fun flavors your
              kids will love.
            </motion.p>

            {/* Buttons */}
            <motion.div 
              className="flex gap-4 pt-4"
              variants={fadeInLeft}
            >
              <motion.button 
                className="px-8 py-4 rounded-full text-base font-semibold bg-gradient-to-r from-[#9ed8e1] to-[#f49cb9] text-white shadow-md hover:scale-105 transition-transform duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Samples
              </motion.button>
              <motion.button 
                className="px-8 py-4 rounded-full text-base font-semibold bg-white text-[#2d3a8c] shadow-md hover:scale-105 transition-transform duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Watch Video
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Right Image Section */}
          <motion.div 
            className="md:w-1/2 mt-12 md:mt-0 flex justify-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={scaleIn}
          >
            <motion.div 
              className="bg-[#e9f4ff] rounded-[3rem] overflow-hidden p-6 md:p-8 max-w-lg w-full"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.img
                src={c2Image}
                alt="Kid brushing teeth"
                className="rounded-[2rem] w-full object-cover"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          </motion.div>
        </div>

        {/* Decorative Background Circles */}
        <motion.div 
          className="absolute -bottom-10 left-10 w-40 h-40 bg-blue-400/30 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute top-10 -right-10 w-56 h-56 bg-blue-300/40 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.6, 0.4]
          }}
          transition={{ 
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
      </section>

      {/* Icons Section */}
      <motion.div 
        className="relative z-10 max-w-6xl mx-auto -mt-20 md:-mt-24"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={iconStagger}
      >
        <div className="bg-white text-[#1a3353] rounded-[2rem] shadow-md grid grid-cols-2 md:grid-cols-4 gap-8 px-8 py-12 md:py-16">
          {/* Card 1 */}
          <motion.div 
            className="flex flex-col items-center text-center space-y-4"
            variants={iconItem}
            whileHover={{ 
              scale: 1.05,
              y: -5,
              transition: { type: "spring", stiffness: 300 }
            }}
          >
            <motion.div 
              className="bg-blue-50 p-4 rounded-2xl"
              whileHover={{ rotate: 5 }}
            >
              <img 
                src={nourishmentIcon} 
                alt="Everyday Nourishment" 
                className="w-12 h-12 object-contain"
              />
            </motion.div>
            <p className="font-semibold text-lg">Everyday Nourishment</p>
          </motion.div>

          {/* Card 2 */}
          <motion.div 
            className="flex flex-col items-center text-center space-y-4"
            variants={iconItem}
            whileHover={{ 
              scale: 1.05,
              y: -5,
              transition: { type: "spring", stiffness: 300 }
            }}
          >
            <motion.div 
              className="bg-blue-50 p-4 rounded-2xl"
              whileHover={{ rotate: -5 }}
            >
              <img 
                src={goodnessIcon} 
                alt="Wholesome Goodness" 
                className="w-12 h-12 object-contain"
              />
            </motion.div>
            <p className="font-semibold text-lg">Wholesome Goodness</p>
          </motion.div>

          {/* Card 3 */}
          <motion.div 
            className="flex flex-col items-center text-center space-y-4"
            variants={iconItem}
            whileHover={{ 
              scale: 1.05,
              y: -5,
              transition: { type: "spring", stiffness: 300 }
            }}
          >
            <motion.div 
              className="bg-blue-50 p-4 rounded-2xl"
              whileHover={{ rotate: 5 }}
            >
              <img 
                src={flavorsIcon} 
                alt="Flavors You Love" 
                className="w-12 h-12 object-contain"
              />
            </motion.div>
            <p className="font-semibold text-lg">Flavors You Love</p>
          </motion.div>

          {/* Card 4 */}
          <motion.div 
            className="flex flex-col items-center text-center space-y-4"
            variants={iconItem}
            whileHover={{ 
              scale: 1.05,
              y: -5,
              transition: { type: "spring", stiffness: 300 }
            }}
          >
            <motion.div 
              className="bg-blue-50 p-4 rounded-2xl"
              whileHover={{ rotate: -5 }}
            >
              <img 
                src={careIcon} 
                alt="Kid-Friendly Care" 
                className="w-12 h-12 object-contain"
              />
            </motion.div>
            <p className="font-semibold text-lg">Kid-Friendly Care</p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default KidsToothpasteSection;